import {QuestionType} from '@agenium-ai/agenium-client-sdk/dist/enum/enum';
import {Question} from '@agenium-ai/agenium-client-sdk/dist/models/question';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Typography from '@material-ui/core/Typography';
import {makeStyles} from '@material-ui/core/styles';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import SmsIcon from '@material-ui/icons/Sms';
import WarningIcon from '@material-ui/icons/Warning';
import csx from 'classnames';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import {observer} from 'mobx-react-lite';
import React, {useEffect, useRef, useState} from 'react';
import {stripHtml} from 'string-strip-html';
import {AgenuimContextType} from '../../../../Core/agenium/ageniumContext';
import RootStore from '../../../../Core/mobx/RootStore';
import HeightInput from '../../../../UI/EmcApplicationQuestions/inputs/HeightInput';
import MultilineInput from '../../../../UI/EmcApplicationQuestions/inputs/MultilineInput';
import MultipleChoiceMultipleInput from '../../../../UI/EmcApplicationQuestions/inputs/MultipleChoiceMultipleInput';
import MultipleChoiceSingleInput from '../../../../UI/EmcApplicationQuestions/inputs/MultipleChoiceSingleInput';
import InputDate from '../../../../UI/EmcApplicationQuestions/inputs/core/InputDate';
import InputPhone from '../../../../UI/EmcApplicationQuestions/inputs/core/InputPhone';
import InputSsn from '../../../../UI/EmcApplicationQuestions/inputs/core/InputSsn';
import InputText from '../../../../UI/EmcApplicationQuestions/inputs/core/InputText';
import {IS_PRODUCTION} from '../../../../constants';
import {updateHubspotProsperDealPolicyNumber} from '../../deal';
import {useInstantApp} from '../common/useInstantApp';
import {useUnderwritingAnalytics} from '@/Core/hooks/useUnderwritingAnalytics';
import UnderwritingReviewScreen from '../UnderwritingReviewScreen';
import {PremiumSourceWithBanking} from './PremiumSourceWithBanking';
import {CalcFocusData} from '../../InstantAppModal';

interface AgeniumApplicationProps {
    quoteResponse: any[];
    dialogContentRef: React.RefObject<unknown>;
    updateLocalCalcfocusData: React.Dispatch<React.SetStateAction<CalcFocusData>>;
    ageniumContext: AgenuimContextType;
    onBack: () => void;
}

const useStyles = makeStyles(() => ({
    title: {
        marginBottom: 40,
        fontFamily: 'LoraRegular',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '32px',
        lineHeight: '120%',
        color: '#456d69',
        '& p': {
            margin: 0,
            padding: 0,
        },
        '& p + p': {
            fontFamily: 'FredokaSemiBold',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '18px',
            lineHeight: '150%',
            color: '#456d69',
        },
    },
    nav: {
        bottom: 0,
        left: 0,
        right: 0,
        margin: '0 0 0 16.666%',
        backgroundColor: '#fff',
        width: 'calc(100% - 16.666%)',
        boxShadow: '6px -4px 10px rgba(0, 0, 0, 0.08)',
        zIndex: 10,
        height: '100px',
        position: 'fixed',
        justifyContent: 'center',
    },
    navContent: {
        display: 'flex',
        width: 'fit-content',
        alignItems: 'center',
        gap: 16,
    },
    input: {
        '& .MuiFormHelperText-root': {
            height: 0,
            fontSize: '14px',
            fontFamily: 'FredokaRegular',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#174943',
            borderWidth: 1,
        },
        '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D131D1',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#174943',
            borderRadius: 1,
        },

        '& .MuiFormLabel-root': {
            color: '#174943',
            '& .Mui-focused': {
                color: '#174943',
            },
        },
        '& .MuiFormHelperText-root.Mui-error': {
            color: '#D131D1',
        },
        '& .MuiOutlinedInput-input': {
            height: 80,
            boxSizing: 'border-box',
            fontFamily: 'FredokaRegular',
            fontSize: 20,
            lineHeight: '120%',
            color: '#174943',
            padding: '18px 24px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            '& ::placeholder': {
                color: '#74928E',
            },
        },
    },
    helperText: {
        fontSize: '14px',
        fontFamily: 'FredokaRegular',
        color: '#174943',
    },
    questionText: {
        marginBottom: 40,
        fontFamily: 'LoraRegular',
        fontWeight: 400,
        fontSize: 32,
        lineHeight: '120%',
        color: '#456d69',
    },
    backBtn: {
        width: 'calc(100% + 16px)',
        margin: '-8px',
        backgroundColor: '#fff',
        justifyContent: 'flex-start',
        top: -8,
        paddingTop: 20,
        paddingBottom: 20,
        position: 'sticky',
        zIndex: 100,
    },
    stickyBorder: {
        borderBottom: '1px solid #D1DBD9',
    },
    showMessage: {
        fontFamily: 'FredokaRegular',
        '& .showMessage_title': {
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '25.6px',
            marginBottom: '8px',
        },
        '& .showMessage_message': {
            fontWeight: 400,
            fontSize: '15px',
            lineHeight: '24px',
        },
        borderRadius: '6px',
        marginBottom: '16px',
        padding: '12px 20px',
        display: 'inline-block',
        color: '#456d69',
    },
    showMessage_error: {
        border: '1px solid #f44336',
        background: 'rgb(253, 236, 234)',
        color: 'rgb(97, 26, 21)',
    },
    showMessage_warning: {
        border: '1px solid #ff9800',
        background: 'rgb(255, 244, 229)',
        color: 'rgb(102, 60, 0)',
    },
    showMessage_note: {
        border: '1px solid #456d69',
    },
    showMessage_text: {
        padding: 0,
    },
    arrowBtn: {
        borderRadius: '50%',
        width: 64,
        height: 64,
        border: '2px solid #174943',
        color: '#174943',
        padding: 0,
        transition: 'background-color 0.2s',
        '&:hover': {
            color: '#4fc878',
            borderColor: 'transparent',
            backgroundColor: '#174943',
        },
        '&.Mui-focusVisible': {
            color: '#4fc878',
            borderColor: 'transparent',
            backgroundColor: '#174943',
        },
        '&.Mui-disabled': {
            color: '#00000042',
            borderColor: 'transparent',
            backgroundColor: '#0000001f',
        },
        '& .MuiSvgIcon-root': {
            width: 28,
            height: 28,
        },
    },
    outlinedBtn: {
        minWidth: '240px',
        minHeight: '64px',
        border: '2px solid #5ae288',
        fontFamily: 'FredokaRegular',
        fontSize: '18px',
        letterSpacing: '0.05em',
        color: '#174943',
        padding: '12px 40px',
        backgroundColor: '#5ae288',
        borderRadius: 102,
        boxSizing: 'border-box',
        transition: 'background-color 0.2s',
        '&:hover': {
            color: '#174943',
            borderColor: 'transparent',
            backgroundColor: '#9cedb7',
        },
        '&.Mui-focusVisible': {
            color: '#174943',
            borderColor: 'transparent',
            backgroundColor: '#9cedb7',
        },
        '&.Mui-disabled': {
            color: '#00000042',
            borderColor: 'transparent',
            backgroundColor: '#0000001f',
        },
    },
    containedBtn: {
        minWidth: '240px',
        minHeight: '64px',
        border: '2px solid #5ae288',
        fontFamily: 'FredokaRegular',
        fontSize: '18px',
        letterSpacing: '0.05em',
        color: '#174943',
        padding: '12px 40px',
        backgroundColor: '#fff',
        borderRadius: 102,
        boxSizing: 'border-box',
        transition: 'background-color 0.2s',
        '&:hover': {
            color: '#174943',
            borderColor: 'transparent',
            backgroundColor: '#9cedb7',
        },
        '&.Mui-focusVisible': {
            color: '#174943',
            borderColor: 'transparent',
            backgroundColor: '#9cedb7',
        },
        '&.Mui-disabled': {
            color: '#00000042',
            borderColor: 'transparent',
            backgroundColor: '#0000001f',
        },
    },
    commonBox: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
        '& svg': {
            width: '20px',
            height: '20px',
        },
        '& p': {
            fontFamily: 'FredokaMedium',
            opacity: 0.9,
            fontSize: 16,
            lineHeight: '140%',
            textTransform: 'uppercase',
            letterSpacing: '0.16em',
        },
    },
    optionalBox: {
        '& svg': {
            fill: '#B9C8C7',
        },
        '& p': {
            color: '#5D807B',
        },
    },
    internalQuestion: {
        '& svg': {
            fill: '#B57D9B',
        },
        '& p': {
            color: '#99863A',
        },
    },
    internal: {
        '& $questionText, & $title, & h1': {
            color: '#794D65',
            fontFamily: 'FredokaMedium',
        },
        '& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
            {
                borderColor: '#B57D9B',
            },
        '& .MuiFormLabel-root': {
            color: '#B57D9B',
        },
        '& $showMessage': {
            color: '#794D65',
            borderColor: '#B57D9B',
        },
    },
}));

const AgeniumApplication: React.FC<AgeniumApplicationProps> = observer(
    ({dialogContentRef, onBack, updateLocalCalcfocusData, ageniumContext}) => {
        const {approve, decline, refer, cancelled} = useInstantApp({
            onCalcFocusApprovalQuoteChange: (data) => updateLocalCalcfocusData(data),
        });
        const {parameterStore} = RootStore.agentPortalStore;
        const contactFormStore = RootStore.agentPortalStore.contactsFormStore;
        const {storeQuestionAndAnswer} = useUnderwritingAnalytics({
            applicationId: ageniumContext.applicationId,
            formId: parameterStore.formid,
        });
        const classes = useStyles();
        const [isBackAvailable, setIsBackAvailable] = useState(true);
        const answerBySection = false;
        const [isPolicyNumberSet, setIsPolicyNumberSet] = useState(false);
        const [hasShadow, setHasShadow] = useState(false);
        const stickyHeaderRef = useRef(null);
        const isInternalQuestion =
            ageniumContext.question?.id === '801bf36f-53ec-405d-a3f6-55f08da2bd70' ||
            (ageniumContext.question?.key != null &&
                [
                    'Case_AgentReport_GI1question',
                    'Case_Replacement_ReadAloud',
                    'Case_Replacement_PositionOfEMC',
                ].includes(ageniumContext.question?.key));
        const isQuestionOptional = ageniumContext.isReady && !ageniumContext.isRequired;
        const isUnderwritingReview = ageniumContext.question?.key === 'Case_UWDecision_Ready';

        useEffect(() => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    setHasShadow(!entry.isIntersecting);
                },
                {rootMargin: '-10px 0px 0px 0px'},
            );

            if (stickyHeaderRef.current) {
                observer.observe(stickyHeaderRef.current);
            }

            return () => observer.disconnect();
        }, []);

        function scrollTop() {
            (dialogContentRef?.current as Element)?.scrollTo({top: 0});
        }

        function checkIfDeclined(question: Question | undefined, withRedirection?: boolean) {
            if (
                question?.questionType === 'DisplayMessage' &&
                question?.displayType === 'Error' &&
                question?.questionText.toLowerCase().includes('application has been declined')
            ) {
                console.log('Found declined question ' + JSON.stringify(question));
                if (withRedirection) decline();
                return true;
            }
            return false;
        }

        const onFormSubmit = async () => {
            // Check if this is the premium source question with banking
            if (ageniumContext.question?.key === 'Case_Applicant_PremiumSource') {
                console.log('Premium source submit - checking banking validation');

                // Check if we have a bank verification function (set by PremiumSourceWithBanking)
                if ((ageniumContext as any).bankVerificationFunction) {
                    const verifyResult = await (ageniumContext as any).bankVerificationFunction();
                    console.log('Bank verification result:', verifyResult);

                    if (!verifyResult.success) {
                        // Show error message
                        if (verifyResult.error) {
                            RootStore.uiStore.showSnackbar(verifyResult.error, 'error');
                        }
                        // Don't proceed if banking verification failed
                        return;
                    }
                }
            }

            if (
                ageniumContext.question &&
                !(
                    ageniumContext.question.validationExpressions.find((t) => t.validationExpression === '.') &&
                    (ageniumContext.currentValue === '' || ageniumContext.currentValue === null) &&
                    ageniumContext.question.questionType !== QuestionType.DisplayMessage
                )
            ) {
                if (ageniumContext.question.questionType === QuestionType.DisplayMessage) {
                    ageniumContext.question.value = 'Value';
                } else {
                    ageniumContext.question.value = ageniumContext.currentValue;
                }

                storeQuestionAndAnswer({
                    questionKey: ageniumContext.question.key,
                    answer: ageniumContext.currentValue,
                    bootstrapped: false,
                });

                await ageniumContext.submitAnswer(ageniumContext.question);
                await ageniumContext.getNextQuestion();
                scrollTop();
                setIsBackAvailable(true);
                scrollTop();
            }
        };

        const onFormRepeatSubmit = async () => {
            if (ageniumContext.question) {
                storeQuestionAndAnswer({
                    questionKey: ageniumContext.question.key,
                    answer: ageniumContext.currentValue,
                    bootstrapped: false,
                });
            }

            await ageniumContext.addRepeatBlock();
            setIsBackAvailable(true);
            scrollTop();
        };

        const cleanHelpText = (rawHelpText: string) => {
            if (!rawHelpText) return null;

            let cleanedHTML = rawHelpText
                .replace(/<[^>]*>Required<\/[^>]*>/gi, '')
                .replace(/\bRequired\b/gi, '')
                .trim();

            return cleanedHTML ? parse(DOMPurify.sanitize(cleanedHTML, {USE_PROFILES: {html: true}})) : null;
        };

        const getQuestionInput = () => {
            const activeQuestion = ageniumContext.question;
            checkIfDeclined(activeQuestion, true);

            // Debug: Check if this is the premium source question
            if (activeQuestion?.key === 'Case_Applicant_PremiumSource') {
                console.log('Premium Source Question detected!', {
                    key: activeQuestion.key,
                    questionType: activeQuestion.questionType,
                    parameterStore: !!parameterStore,
                    contactFormStore: !!contactFormStore,
                });
            }

            const helpText = cleanHelpText(activeQuestion?.helpText || '');

            const activeQuestionText = stripHtml(activeQuestion?.questionText ?? '').result.replaceAll('&nbsp;', ' ');

            const needsExtendedText = !activeQuestionText.includes(':') && !activeQuestionText.includes('?');
            const extendedQuestionText = (
                <Typography component="h2" className={classes.questionText}>
                    {needsExtendedText ? `Please provide ${activeQuestionText}` : activeQuestionText}
                </Typography>
            );

            const questionText =
                activeQuestionText.length * 7 > 580 ? (
                    <Typography component="h1" variant="h4" classes={{root: classes.title}}>
                        {parse(DOMPurify.sanitize(activeQuestionText, {USE_PROFILES: {html: true}}))}
                    </Typography>
                ) : null;

            switch (activeQuestion?.questionType) {
                case QuestionType.ShortAnswer:
                    return (
                        <>
                            {questionText || extendedQuestionText}
                            <InputText
                                name={activeQuestion.key}
                                onChange={(e) => ageniumContext.setCurrentValue(e.target.value)}
                                value={(ageniumContext.currentValue as string) ?? ''}
                                label={stripHtml(activeQuestion.questionText).result}
                                parentClass="form-group"
                                placeholder={stripHtml(activeQuestion.questionText).result}
                                InputLabelProps={{shrink: true}}
                                classes={{root: classes.input}}
                            />
                            <Typography component="p" className={classes.helperText}>
                                {helpText}
                            </Typography>
                        </>
                    );
                case QuestionType.Numeric:
                    return (
                        <>
                            {questionText || extendedQuestionText}
                            <InputText
                                name={activeQuestion.key}
                                onChange={(e) => ageniumContext.setCurrentValue(e.target.value)}
                                value={(ageniumContext.currentValue as string) ?? ''}
                                label={stripHtml(activeQuestion.questionText).result}
                                parentClass="form-group"
                                mask={{
                                    ...(ageniumContext.question?.validationExpressions?.some((t) =>
                                        t.validationExpression.includes('\\.\\d+'),
                                    )
                                        ? {mask: /^\d*\.?\d*$/}
                                        : {mask: Number}),
                                }}
                                placeholder={stripHtml(activeQuestion.questionText).result}
                                InputLabelProps={{shrink: true}}
                                classes={{root: classes.input}}
                            />
                            <Typography component="p" className={classes.helperText}>
                                {helpText}
                            </Typography>
                        </>
                    );
                case QuestionType.Email:
                    return (
                        <>
                            {questionText || (
                                <Typography component="h2" className={classes.questionText}>
                                    {`Please provide ${activeQuestionText}`}
                                </Typography>
                            )}
                            <InputText
                                type="email"
                                name={activeQuestion.key}
                                onChange={(e) => ageniumContext.setCurrentValue(e.target.value)}
                                value={(ageniumContext.currentValue as string) ?? ''}
                                label={stripHtml(activeQuestion.questionText).result}
                                parentClass="form-group"
                                placeholder="example@example.com"
                                InputLabelProps={{shrink: true}}
                                classes={{root: classes.input}}
                            />
                            <Typography component="p" className={classes.helperText}>
                                {helpText}
                            </Typography>
                        </>
                    );
                case QuestionType.MultipleChoiceSingleAnswer:
                    // Check if this is the premium source question
                    if (activeQuestion?.key === 'Case_Applicant_PremiumSource' && parameterStore && contactFormStore) {
                        return (
                            <PremiumSourceWithBanking
                                question={activeQuestion}
                                ageniumContext={ageniumContext}
                                parameterStore={parameterStore}
                                contactFormStore={contactFormStore}
                                classes={classes}
                            />
                        );
                    }
                    return ageniumContext.applicationId && isUnderwritingReview ? (
                        <>
                            <UnderwritingReviewScreen ageniumContext={ageniumContext} />
                        </>
                    ) : (
                        <>
                            <MultipleChoiceSingleInput
                                value={ageniumContext.currentValue}
                                onChange={(value) => ageniumContext.setCurrentValue(value)}
                                question={activeQuestion}
                                isInternalQuestion={isInternalQuestion}
                            />
                            <Typography component="p" className={classes.helperText}>
                                {helpText}
                            </Typography>
                        </>
                    );
                case QuestionType.PhoneNumber:
                    return (
                        <>
                            {questionText || (
                                <Typography component="h2" className={classes.questionText}>
                                    {`Please provide ${activeQuestionText.replaceAll(':', '')}`}
                                </Typography>
                            )}
                            <InputPhone
                                label={
                                    stripHtml(activeQuestion.questionText.replaceAll(':', '')).result ?? 'Phone Number'
                                }
                                value={ageniumContext.currentValue ?? ''}
                                onChange={(value: string) => ageniumContext.setCurrentValue(value)}
                                classes={{root: classes.input}}
                            />
                            <Typography component="p" className={classes.helperText}>
                                {helpText}
                            </Typography>
                        </>
                    );
                case QuestionType.SSN:
                    return (
                        <>
                            {questionText || (
                                <Typography component="h2" className={classes.questionText}>
                                    {`Please provide ${activeQuestionText}`}
                                </Typography>
                            )}
                            <InputSsn
                                label={stripHtml(activeQuestion.questionText).result}
                                onChange={(value: any) => ageniumContext.setCurrentValue(value)}
                                value={ageniumContext.currentValue ?? ''}
                                classes={{root: classes.input}}
                            />
                            <Typography component="p" className={classes.helperText}>
                                {helpText}
                            </Typography>
                        </>
                    );
                case QuestionType.Height:
                    return (
                        <>
                            <HeightInput
                                value={(ageniumContext.currentValue as number) ?? 0}
                                onChange={ageniumContext.setCurrentValue}
                                question={activeQuestion}
                            />
                            <Typography component="p" className={classes.helperText}>
                                {helpText}
                            </Typography>
                        </>
                    );
                case QuestionType.Date:
                    return (
                        <>
                            {questionText || extendedQuestionText}
                            <InputDate
                                label={stripHtml(activeQuestion.questionText).result}
                                onChange={(e) => ageniumContext.setCurrentValue(e.target.value)}
                                classes={{root: classes.input}}
                                value={(ageniumContext.currentValue as string) ?? ''}
                                format={
                                    activeQuestion?.dateFormat === 'YYYY-MM' ? 'MM/YYYY' : activeQuestion?.dateFormat
                                }
                            />
                            <Typography component="p" className={classes.helperText}>
                                {helpText}
                            </Typography>
                        </>
                    );
                case QuestionType.MultiLine:
                    return (
                        <>
                            {questionText || extendedQuestionText}
                            <MultilineInput
                                value={(ageniumContext.currentValue as string) ?? ''}
                                onChange={ageniumContext.setCurrentValue}
                                question={activeQuestion}
                                classes={{root: classes.input}}
                            />
                            <Typography component="p" className={classes.helperText}>
                                {helpText}
                            </Typography>
                        </>
                    );
                case QuestionType.MultipleChoiceMultipleAnswer:
                    // Check if this is the premium source question
                    if (activeQuestion?.key === 'Case_Applicant_PremiumSource' && parameterStore && contactFormStore) {
                        return (
                            <PremiumSourceWithBanking
                                question={activeQuestion}
                                ageniumContext={ageniumContext}
                                parameterStore={parameterStore}
                                contactFormStore={contactFormStore}
                                classes={classes}
                            />
                        );
                    }
                    return (
                        <>
                            <MultipleChoiceMultipleInput
                                value={ageniumContext.currentValue as string}
                                onChange={ageniumContext.setCurrentValue}
                                question={activeQuestion}
                            />
                            <Typography component="p" className={classes.helperText}>
                                {helpText}
                            </Typography>
                        </>
                    );
                case QuestionType.DisplayMessage:
                    return (
                        <>
                            <Typography component="h1" variant="h4" style={{marginBottom: 4}}>
                                {activeQuestion.displayType}
                            </Typography>
                            <Typography component="h1" variant="h4" style={{marginBottom: 4}}>
                                {parse(
                                    DOMPurify.sanitize(activeQuestion.questionText, {
                                        USE_PROFILES: {html: true},
                                    }),
                                )}
                            </Typography>
                            <Typography component="p" className={classes.helperText}>
                                {helpText}
                            </Typography>
                        </>
                    );
                default:
                    return <></>;
            }
        };

        const onBackQuestion = async () => {
            await ageniumContext.getPreviousQuestion().catch((error) => {
                if (error?.message.includes('Unable to find previous question in the schema.'))
                    setIsBackAvailable(false);
                scrollTop();
            });
            scrollTop();
        };

        useEffect(() => {
            if (ageniumContext.applicationData) {
                const decision = ageniumContext.applicationData.find((t) => t.key === 'Case_UWDecision_Overall');
                parameterStore.prosperDecision = decision?.value;
                if (!!decision && decision !== undefined) {
                    // dump all data for debugging purposes
                    console.log(`Decision found ${decision}`);
                    console.log(ageniumContext.applicationData);

                    if (!decision?.value?.match(/referr/gi) && decision?.value?.match(/(?<!p)refer/gi)) {
                        refer();
                    } else if (decision?.value?.toLowerCase()?.match(/postpone|decline(d?)/gi)) {
                        decline();
                    } else if (decision?.value?.toLowerCase()?.match(/withdrawn|ineligible|incomplete/gi)) {
                        cancelled();
                    } else {
                        approve();
                    }
                }
            }
        }, [ageniumContext.applicationData]);

        useEffect(() => {
            if (!isPolicyNumberSet && !!ageniumContext.policyNumber) {
                setIsPolicyNumberSet(true);
                updateHubspotProsperDealPolicyNumber(ageniumContext.policyNumber);
            }
        }, [ageniumContext.policyNumber]);

        useEffect(() => {
            ageniumContext.initSdk();
        }, []);

        return (
            <Grid container spacing={2}>
                <Grid item xs={3} style={{marginTop: 20}}>
                    Sections
                    <List>
                        {ageniumContext.sections
                            ?.sort((a, b) => a.sortOrder - b.sortOrder)
                            .map((section) => (
                                <ListItem key={section.id}>
                                    {section.id === ageniumContext.question?.sectionId && (
                                        <strong>{section.name}</strong>
                                    )}
                                    {section.id !== ageniumContext.question?.sectionId && <>{section.name}</>}
                                </ListItem>
                            ))}
                    </List>
                </Grid>
                {!answerBySection && (
                    <Grid
                        item
                        xs={9}
                        style={{position: 'relative', paddingBottom: 113}}
                        className={csx({[classes.internal]: isInternalQuestion})}
                    >
                        <Box>
                            <div ref={stickyHeaderRef} />
                            <Box style={{maxWidth: '900px', padding: '32px 0'}}>
                                {isQuestionOptional && (
                                    <Box className={csx(classes.commonBox, classes.optionalBox)}>
                                        <SmsIcon />
                                        <Typography>Optional</Typography>
                                    </Box>
                                )}
                                {isInternalQuestion && (
                                    <Box className={csx(classes.commonBox, classes.internalQuestion)}>
                                        <WarningIcon />
                                        <Typography>internal question only - do not ask client</Typography>
                                    </Box>
                                )}
                                {getQuestionInput()}
                            </Box>
                            {ageniumContext.showMessage && (
                                <Box
                                    className={`${classes.showMessage} ${classes[`showMessage_${ageniumContext.showMessage.variant}`]}`}
                                >
                                    {ageniumContext.showMessage?.title && (
                                        <Typography component="p" className="showMessage_title">
                                            {ageniumContext.showMessage.title}
                                        </Typography>
                                    )}
                                    <Typography component="p" className="showMessage_message">
                                        {ageniumContext.showMessage.message}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <Box display="flex" alignItems="center" className={classes.nav}>
                            <Box className={classes.navContent}>
                                <Box>
                                    <Button
                                        className={classes.arrowBtn}
                                        color="primary"
                                        disableRipple
                                        disableElevation
                                        variant="outlined"
                                        disabled={
                                            !isBackAvailable || ageniumContext.isLoading() || !ageniumContext.question
                                        }
                                        onClick={onBackQuestion}
                                    >
                                        {ageniumContext.isLoading() ? (
                                            <CircularProgress style={{height: '1em', width: '1em', color: 'inherit'}} />
                                        ) : (
                                            <KeyboardBackspaceIcon />
                                        )}
                                    </Button>
                                </Box>
                                <Button
                                    className={classes.outlinedBtn}
                                    type="submit"
                                    variant="contained"
                                    disableElevation
                                    disableRipple
                                    color="primary"
                                    disabled={
                                        !ageniumContext.isValidated ||
                                        ageniumContext.isLoading() ||
                                        !ageniumContext.question ||
                                        (ageniumContext.question?.key === 'Case_Applicant_PremiumSource' &&
                                            (ageniumContext as any).canProceedWithBanking &&
                                            !(ageniumContext as any).canProceedWithBanking())
                                    }
                                    onClick={onFormSubmit}
                                >
                                    {ageniumContext.isLoading() && (
                                        <CircularProgress
                                            style={{
                                                height: '1em',
                                                width: '1em',
                                                marginRight: '0.5em',
                                                color: 'inherit',
                                            }}
                                        />
                                    )}
                                    Next
                                </Button>
                                {ageniumContext.isRepeatBlock && (
                                    <Button
                                        className={classes.containedBtn}
                                        type="submit"
                                        disableElevation
                                        variant="contained"
                                        color="primary"
                                        disabled={ageniumContext.isLoading() || !ageniumContext.question}
                                        onClick={onFormRepeatSubmit}
                                    >
                                        {ageniumContext.isLoading() && (
                                            <CircularProgress
                                                style={{
                                                    height: '1em',
                                                    width: '1em',
                                                    marginRight: '0.5em',
                                                    color: 'inherit',
                                                }}
                                            />
                                        )}
                                        Add another
                                    </Button>
                                )}
                            </Box>
                        </Box>{' '}
                        <Box style={{fontSize: 10, marginTop: '40px'}}>
                            <pre
                                style={{
                                    overflowX: 'auto',
                                    maxWidth: '100%',
                                    wordWrap: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                }}
                            >
                                <code style={{color: '#00f'}}>
                                    <p>
                                        <b style={{color: '#f50'}}>Application Id:</b> {ageniumContext.applicationId}
                                    </p>
                                    <p>
                                        <b style={{color: '#f50'}}>Application Number:</b>{' '}
                                        {ageniumContext.applicationNumber}
                                    </p>
                                    <p>
                                        <b style={{color: '#f50'}}>Policy Number:</b> {ageniumContext.policyNumber}
                                    </p>
                                    {!IS_PRODUCTION && (
                                        <>
                                            question:
                                            {Object.keys(ageniumContext.question ?? {}).map((k, i) => {
                                                return (
                                                    <div key={i}>
                                                        {k}:{' '}
                                                        {k === 'value' ? (
                                                            <span style={{color: '#f0f', fontWeight: 'bold'}}>
                                                                {JSON.stringify(ageniumContext.currentValue, null, 2)}
                                                            </span>
                                                        ) : (
                                                            JSON.stringify((ageniumContext.question as any)[k], null, 2)
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            <p></p>
                                            applicationData:
                                            {Object.keys(ageniumContext.applicationData ?? {}).map((k, i) => {
                                                return (
                                                    <div key={i}>
                                                        {k}:{' '}
                                                        {JSON.stringify(
                                                            (ageniumContext.applicationData as any)[k],
                                                            null,
                                                            2,
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </>
                                    )}
                                </code>
                            </pre>
                        </Box>
                    </Grid>
                )}

                {answerBySection && <>By Section</>}
            </Grid>
        );
    },
);

export default AgeniumApplication;
